import { useProductFlashSale } from '@/hooks/react-query/flash-sale/use-flash-sale'
import { useCoolClubPrice } from '@/hooks/use-coolclub-price'
import { useProductDetail } from '@/hooks/zustands/use-product-detail'
import { useTrackEvent } from '@/lib/gtm/track-event'
import { ETrackingEvent, TTrackingEcommerce } from '@/lib/gtm/type'
import { stringToSlug } from '@/lib/utils'
import { BreadCrumbItem, Option } from '@/types/product'
import { ProductDetailFormatted } from '@/types/product-detail'
import { sendGTMEvent } from '@next/third-parties/google'
import dayjs from 'dayjs'
import { usePathname, useSearchParams } from 'next/navigation'
import {
    ChangeEvent,
    KeyboardEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useProductDetailSafe } from '../context/product-detail-context'

/**
 * Lấy sản phẩm con trong set đồ ứng với option combo tại `optionIndex`.
 * optionIndex bao gồm cả color option, nhưng setdo_variants chỉ chứa các sản phẩm con (không có color)
 * nên cần trừ đi số color options trước index hiện tại để lấy đúng setdo_variants index
 */
export const getSetdoPart = (
  product: ProductDetailFormatted,
  optionIndex: number
) => {
  const comboOptions = product.combo?.options || []
  const colorsBefore = comboOptions
    .slice(0, optionIndex)
    .filter((o) => o?.option_id?.includes('color')).length

  return product.setdo_variants?.[optionIndex - colorsBefore]
}

export const useProductDetailStore = () => {
  const {
    product,
    colorSelected,
    setColorSelected,
    setColorOptionActive,
    sizeSelected,
    setSizeSelected,
    sizeOptions,
    sortedColorOptions,
    comboSelectedInfo,
    setComboSelectedInfo,
  } = useProductDetailSafe()

  const pathname = usePathname()
  const searchParams = useSearchParams()
  const trackEvent = useTrackEvent<Partial<TTrackingEcommerce>>()

  // State for variants UI
  const [isOpen, setIsOpen] = useState(false)
  const [colorsPerRow, setColorsPerRow] = useState(5)

  // Track if size guide drawer is open to prevent URL updates that close it
  const [isSizeGuideDrawerOpen, setIsSizeGuideDrawerOpen] = useState(false)

  // Ref to track if color has been initialized to prevent re-initialization
  // that causes unwanted URL updates and component re-renders
  const hasInitializedColorRef = useRef(false)

  /**
   * Size selection logic
   */
  const isDisableSize = useCallback(
    (size: string) => {
      if (!colorSelected || !product.mapped_variants) {
        return true
      }

      const colorKey = Object.keys(product.mapped_variants).find(
        (key) => stringToSlug(key) === colorSelected
      )

      if (!colorKey) {
        return true
      }

      const variantsOfCurrentColor = product.mapped_variants[colorKey]

      const variantWithSize = variantsOfCurrentColor.find(
        (variant) => variant.size === size
      )

      if (!variantWithSize || variantWithSize.isDisable) {
        return true
      }

      return false
    },
    [colorSelected, product.mapped_variants]
  )

  // Auto-select first valid size khi chưa chọn size nào.
  // Đã chọn size rồi thì đổi màu vẫn giữ nguyên, kể cả khi màu mới hết size đó
  const updateSizeOnColorChange = useCallback(() => {
    if (!colorSelected || !product.mapped_variants) return

    if (!sizeSelected) {
      const firstValidSize = sizeOptions.find((size) => !isDisableSize(size))
      if (firstValidSize) {
        setSizeSelected(firstValidSize)
      }
    }
  }, [
    colorSelected,
    sizeSelected,
    setSizeSelected,
    sizeOptions,
    isDisableSize,
    product.mapped_variants,
  ])

  useEffect(() => {
    updateSizeOnColorChange()
  }, [updateSizeOnColorChange])

  /**
   * Combo size selection logic
   */
  const isDisableComboSize = useCallback(
    (size: string, index: number) => {
      // Nếu là sản phẩm set đồ (setdo), kiểm tra tồn kho của từng món độc lập (chỉ theo size - option2)
      if (product.is_setdo && product.setdo_variants) {
        const partData = getSetdoPart(product, index)
        if (partData) {
          // Tích "Áp dụng ngưỡng lưu kho tối thiểu" thì chỉ bán khi tồn kho > 5
          const minQuantity = product.apply_allowance_inventory ? 5 : 0
          const hasStock = partData.variants.some(
            (v) => v.option2 === size && v.quantity > minQuantity
          )
          return !hasStock
        }
      }

      // Fallback logic cũ cho combo thông thường
      const hasAnyValidVariant = product.combo?.variants.some((v) => {
        const currentSize = (v as unknown as Record<string, string>)[
          `option${index + 1}`
        ]
        return currentSize === size && !v.isDisable
      })

      return !hasAnyValidVariant
    },
    [product]
  )

  const handleComboSizeChange = useCallback(
    (value: string, optionIndex: number) => {
      // Khi chọn size độc lập, chỉ cập nhật giá trị của option đó
      setComboSelectedInfo({
        ...comboSelectedInfo,
        [`option${optionIndex + 1}`]: value,
      })
    },
    [comboSelectedInfo, setComboSelectedInfo]
  )

  /**
   * Color selection logic
   */
  const handleChangeColor = useCallback(
    (variant: Option) => {
      const slug = stringToSlug(variant.title)
      setColorSelected(slug)
      setColorOptionActive(variant)
      // Use window.history.replaceState instead of router.replace to avoid re-renders
      // that can close modals/drawers
      const params = new URLSearchParams(searchParams.toString())
      params.set('color', slug)
      const newUrl = `${pathname}?${params.toString()}`
      window.history.replaceState(
        { ...window.history.state, as: newUrl, url: newUrl },
        '',
        newUrl
      )
    },
    [pathname, setColorSelected, setColorOptionActive, searchParams]
  )

  // Initialize color from URL or first color
  const initializeColorFromURL = useCallback(() => {
    const colorFromURL = searchParams.get('color')

    const matched = sortedColorOptions.find(
      (option: Option) => stringToSlug(option.title) === colorFromURL
    )

    if (colorFromURL && matched) {
      // If there's a color from URL and it matches, use it
      setColorSelected(colorFromURL)
      setColorOptionActive(matched)
      hasInitializedColorRef.current = true
    } else if (sortedColorOptions.length > 0) {
      // If color from URL doesn't match, or there's no color from URL → fallback to first color
      const firstColor = sortedColorOptions[0]
      const slug = stringToSlug(firstColor.title)

      setColorSelected(slug)
      setColorOptionActive(firstColor)

      // Only update URL on first initialization and when drawer is NOT open
      // to prevent unwanted re-renders that can close modals/drawers (e.g., size guide popup)
      if (
        !hasInitializedColorRef.current &&
        !isSizeGuideDrawerOpen &&
        colorFromURL !== slug
      ) {
        // Use window.history.replaceState instead of router.replace to avoid re-renders
        // that can close modals/drawers (e.g., size guide popup)
        const params = new URLSearchParams(searchParams.toString())
        params.set('color', slug)
        const newUrl = `${pathname}?${params.toString()}`
        window.history.replaceState(
          { ...window.history.state, as: newUrl, url: newUrl },
          '',
          newUrl
        )
        hasInitializedColorRef.current = true
      }
    }
  }, [
    searchParams,
    sortedColorOptions,
    setColorSelected,
    setColorOptionActive,
    pathname,
    isSizeGuideDrawerOpen,
  ])

  /**
   * Tracking logic
   */
  function convertBreadcrumbToCategory(bread_crumb: BreadCrumbItem[]) {
    return bread_crumb.reduce(
      (acc, item, index) => {
        const key = index === 0 ? 'item_category' : `item_category${index + 1}`
        acc[key] = item.title
        return acc
      },
      {} as Record<string, string>
    )
  }

  const handleTracking = useCallback(
    (selectedColor: string) => {
      const data = {
        event: ETrackingEvent.ViewItem,
        ecommerce: {
          currency: 'VND',
          value: product.regular_price,
          items: [
            {
              item_id: product.id,
              item_name: product.title,
              price: product.regular_price,
              quantity: 1,
              item_brand: 'Coolmate',
              href: product.seo_alias,
              ...convertBreadcrumbToCategory(product.bread_crumb),
              discount: product.sale_percent,
              item_rating: product.review.ratingValue,
              item_variant: selectedColor,
              item_url: product.seo_alias,
              item_image:
                product.icon_thumbnail?.src || product.images?.[0]?.src,
            },
          ],
        },
      }

      sendGTMEvent({ ecommerce: null })
      trackEvent(data)
    },
    [product, trackEvent, product]
  )

  // Fallback and handle when colorSelected changes
  const handleColorSelectedChange = useCallback(() => {
    if (!colorSelected) return

    const matched = sortedColorOptions.find(
      (option: Option) => stringToSlug(option.title) === colorSelected
    )

    if (matched) {
      setColorOptionActive(matched)
    } else if (sortedColorOptions.length > 0) {
      // If no matching color is found, use the first color
      const fallback = sortedColorOptions[0]
      setColorSelected(stringToSlug(fallback.title))
      setColorOptionActive(fallback)
    }
    handleTracking(colorSelected)
  }, [
    colorSelected,
    sortedColorOptions,
    setColorOptionActive,
    setColorSelected,
    handleTracking,
  ])

  // Initialize color from URL on component mount
  useEffect(() => {
    initializeColorFromURL()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, product, pathname, sortedColorOptions])

  // Handle color selection changes
  useEffect(() => {
    handleColorSelectedChange()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorSelected, sortedColorOptions])

  /**
   * Quantity adjustment logic
   */
  const { quantity, setQuantity, handleAddToCart, isPendingAddToCart } =
    useProductDetailSafe()

  const handleQuantityChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      // Remove any non-digit characters
      const value = e.target.value.replace(/\D/g, '')

      // Prevent leading zeros
      if (value.length > 1 && value[0] === '0') {
        setQuantity(parseInt(value.slice(1)))
        return
      }

      // Don't allow empty value
      if (!value) {
        setQuantity(1)
        return
      }

      // Convert to number and validate range
      const numValue = parseInt(value)
      if (numValue > 999) {
        setQuantity(999)
      } else {
        setQuantity(numValue)
      }
    },
    [setQuantity]
  )

  const handleIncrement = useCallback(() => {
    if (quantity < 999) {
      setQuantity(quantity + 1)
    }
  }, [quantity, setQuantity])

  const handleDecrement = useCallback(() => {
    if (quantity > 1) {
      setQuantity(quantity - 1)
    }
  }, [quantity, setQuantity])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        handleIncrement()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        handleDecrement()
      }
    },
    [handleIncrement, handleDecrement]
  )

  /**
   * Flash Sale and product pricing logic
   */
  const productFlashSale = useProductFlashSale(product.id)

  // Kiểm tra xem có Flash Sale không, phải nằm trong khoảng thời gian Flash Sale so với hiện tại
  const isFlashSale =
    productFlashSale?.endsAt &&
    productFlashSale?.product &&
    dayjs(productFlashSale?.endsAt).isAfter(dayjs()) &&
    dayjs(productFlashSale?.startsAt).isBefore(dayjs())

  // Xử lý giá sản phẩm với hook useCoolClubPrice
  // Nếu có Flash Sale, xử lý giá Flash Sale trước
  const { productData: baseProductData, isCoolClubSale } =
    useCoolClubPrice(product)

  // Kết hợp giá Flash Sale nếu có
  const productData = useMemo(() => {
    return isFlashSale
      ? {
          ...baseProductData,
          regular_price: productFlashSale.product.regular_price,
          compare_price: productFlashSale.product.compare_price,
          sale_percent: productFlashSale.product.compare_price
            ? 100 -
              Math.round(
                (productFlashSale.product.regular_price /
                  productFlashSale.product.compare_price) *
                  100
              )
            : 0,
        }
      : baseProductData
  }, [isFlashSale, baseProductData, productFlashSale])

  /**
   * Add to cart logic
   */
  const submitAddToCart = useCallback(
    (payload: {
      regularPrice: number
      comparePrice: number
      giftId?: string
      giftOptionId?: string
    }) => {
      handleAddToCart(payload)
    },
    [handleAddToCart]
  )

  // Get gift data from product detail context
  const { giftData } = useProductDetail()

  // Shared handleAddToCartClick function for both components
  const handleAddToCartClick = useCallback(
    (options?: { skipPendingCheck?: boolean }) => {
      // Check if add to cart is already in progress
      if (!options?.skipPendingCheck && isPendingAddToCart) return

      // Use prices from productData
      submitAddToCart({
        regularPrice: productData.regular_price,
        comparePrice: productData.compare_price || 0,
        giftId: giftData?.id || '',
        giftOptionId: giftData?.optionId || '',
      })
    },
    [isPendingAddToCart, submitAddToCart, giftData, productData]
  )

  return {
    // Size logic
    isDisableSize,
    updateSizeOnColorChange,

    // Combo logic
    isDisableComboSize,
    handleComboSizeChange,

    // Color logic
    handleChangeColor,
    initializeColorFromURL,
    handleColorSelectedChange,

    // UI state for variants
    isOpen,
    setIsOpen,
    colorsPerRow,
    setColorsPerRow,

    // Size guide drawer state
    isSizeGuideDrawerOpen,
    setIsSizeGuideDrawerOpen,

    // Quantity logic
    quantity,
    handleQuantityChange,
    handleIncrement,
    handleDecrement,
    handleKeyDown,

    // Add to cart logic
    submitAddToCart,
    isPendingAddToCart,
    handleAddToCartClick,

    // Flash Sale and pricing logic
    isFlashSale,
    productData,
    isCoolClubSale,
    productFlashSale,

    // Tracking
    handleTracking,
    convertBreadcrumbToCategory,
  }
}
